import React from 'react'
import { Attachment, Container } from '../../components'
import {
    Divider,
    IconButton,
    Modal,
    Paper,
    Slide,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import {
    AreaConfig,
    InventoryConfig,
    Issue,
    Lease,
    ModelListMap,
    ModelMap,
    Unit,
} from '../../models'
import { toMMDDYYYY } from '../../helpers'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    open: boolean
    handleClose: () => void
    areaConfigMap: ModelMap<AreaConfig>
    invConfigMap: ModelMap<InventoryConfig>
    unitMap: ModelMap<Unit>
    leases: Lease[]
    isLoading: boolean
}

export const CurrentTenantsModal = (props: Props) => {
    const {
        open,
        handleClose,
        leases,
        areaConfigMap,
        unitMap,
        invConfigMap,
        isLoading,
    } = props

    const theme = useTheme()

    // Calculate total issues across all leases and their children
    const calculateIssueCount = (lease: Lease): number => {
        const mainLeaseIssues =
            lease.tenant_move_in_inspection?.inventory_inspection?.reduce(
                (sum, inv_ins) => {
                    return sum + (inv_ins.issue?.length || 0)
                },
                0,
            ) || 0

        const childLeaseIssues = (lease.children || []).reduce(
            (sum, childLease) => {
                return sum + calculateIssueCount(childLease)
            },
            0,
        )

        return mainLeaseIssues + childLeaseIssues
    }

    const totalIssueCount = leases.reduce(
        (total, lease) => total + calculateIssueCount(lease),
        0,
    )

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        display: 'flex',
                        overflowY: 'hidden',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        outline: 'none',
                        borderRadius: '20px',
                        maxWidth: '1000px',
                        width: 'calc(100vw - 200px)',
                        height: 'calc(100vh - 200px)',
                        padding: theme.spacing(1, 4, 1, 4),
                    }}
                >
                    <Container
                        style={{
                            padding: theme.spacing(3, 0, 4, 2),
                            flex: 1,
                            overflowY: 'scroll',
                        }}
                        direction="column"
                    >
                        {/* Header */}
                        <Container
                            style={{
                                marginBottom: theme.spacing(1),
                                flexDirection: 'column',
                            }}
                        >
                            <Container>
                                <span
                                    style={{
                                        ...typographyStyle(theme.typography.h5),
                                        fontWeight: 'bold',
                                        alignSelf: 'center',
                                    }}
                                >
                                    Current Tenants: ({totalIssueCount}) Total
                                    Issues Found
                                </span>
                                <div style={{ flex: 1 }} />
                                <Tooltip title="Close">
                                    <IconButton onClick={handleClose}>
                                        <Close fontSize="large" />
                                    </IconButton>
                                </Tooltip>
                            </Container>
                        </Container>

                        <Divider />

                        {/* Body */}
                        <Container
                            style={{
                                flex: 1,
                                overflowY: 'scroll',
                            }}
                            direction="column"
                        >
                            {isLoading ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.h6,
                                            ),
                                        }}
                                    >
                                        Loading tenant data...
                                    </span>
                                </Container>
                            ) : leases.length === 0 ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.h6,
                                            ),
                                        }}
                                    >
                                        No current tenants found
                                    </span>
                                </Container>
                            ) : (
                                leases.map((lease) => {
                                    const renderLeaseContent = (
                                        lease: Lease,
                                    ) => {
                                        const areaNameAndLabel =
                                            areaConfigMap[
                                                lease.area.area_config ?? -1
                                            ]?.name +
                                            ' (' +
                                            lease.area.area_label +
                                            ')'

                                        const invConfigList: InventoryConfig[] = []
                                        const moveInIssues: ModelListMap<Issue> = {}

                                        lease.tenant_move_in_inspection?.inventory_inspection?.forEach(
                                            (inv_ins) => {
                                                const config =
                                                    invConfigMap[
                                                        inv_ins.inventory_config
                                                    ]
                                                if (config) {
                                                    invConfigList.push(config)
                                                }
                                                moveInIssues[
                                                    inv_ins.inventory_config
                                                ] = []
                                                inv_ins.issue?.forEach(
                                                    (iss) => {
                                                        moveInIssues[
                                                            inv_ins
                                                                .inventory_config
                                                        ]?.push(iss)
                                                    },
                                                )
                                            },
                                        )

                                        return (
                                            <Container
                                                style={{
                                                    flexDirection: 'column',
                                                    padding: theme.spacing(2),
                                                }}
                                            >
                                                <Container
                                                    style={{
                                                        borderRadius:
                                                            theme.shape
                                                                .borderRadius,
                                                        padding: theme.spacing(
                                                            1,
                                                        ),
                                                        backgroundColor:
                                                            theme.palette
                                                                .grey[100],
                                                        marginBottom: theme.spacing(
                                                            2,
                                                        ),
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            ...typographyStyle(
                                                                theme.typography
                                                                    .subtitle1,
                                                            ),
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Area: {areaNameAndLabel}
                                                    </span>

                                                    {lease
                                                        .tenant_move_in_inspection
                                                        ?.auto_completed ===
                                                        1 && (
                                                        <span
                                                            style={{
                                                                ...typographyStyle(
                                                                    theme
                                                                        .typography
                                                                        .body2,
                                                                ),
                                                                marginLeft:
                                                                    'auto',
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .secondary
                                                                        .main,
                                                                fontWeight:
                                                                    'bold',
                                                            }}
                                                        >
                                                            Auto Completed
                                                        </span>
                                                    )}
                                                </Container>

                                                {invConfigList.length === 0 ? (
                                                    <Container
                                                        style={{
                                                            padding: theme.spacing(
                                                                2,
                                                            ),
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'center',
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                ...typographyStyle(
                                                                    theme
                                                                        .typography
                                                                        .body1,
                                                                ),
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .secondary,
                                                            }}
                                                        >
                                                            No issues found for
                                                            this area
                                                        </span>
                                                    </Container>
                                                ) : (
                                                    invConfigList.map(
                                                        (invConfig) => (
                                                            <Container
                                                                key={`INV_${invConfig.id}`}
                                                                style={{
                                                                    marginBottom: theme.spacing(
                                                                        2,
                                                                    ),
                                                                    flexDirection:
                                                                        'column',
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        ...typographyStyle(
                                                                            theme
                                                                                .typography
                                                                                .subtitle2,
                                                                        ),
                                                                        fontWeight:
                                                                            'bold',
                                                                        marginBottom: theme.spacing(
                                                                            1,
                                                                        ),
                                                                    }}
                                                                >
                                                                    {
                                                                        invConfig.name
                                                                    }
                                                                </span>

                                                                {moveInIssues[
                                                                    invConfig.id
                                                                ]?.map(
                                                                    (issue) => {
                                                                        const displayName = issue.issue_config
                                                                            ? issue
                                                                                  .issue_config
                                                                                  .name
                                                                            : issue.description
                                                                        return (
                                                                            <Container
                                                                                key={`ISSUE_${issue.id}`}
                                                                                style={{
                                                                                    flexDirection:
                                                                                        'row',
                                                                                    margin: theme.spacing(
                                                                                        1,
                                                                                    ),
                                                                                    border: `1px solid ${theme.palette.grey[400]}`,
                                                                                    borderRadius:
                                                                                        theme
                                                                                            .shape
                                                                                            .borderRadius,
                                                                                    minHeight: 200,
                                                                                    overflowY:
                                                                                        'scroll',
                                                                                    alignItems:
                                                                                        'center',
                                                                                }}
                                                                            >
                                                                                <span
                                                                                    style={{
                                                                                        ...typographyStyle(
                                                                                            theme
                                                                                                .typography
                                                                                                .body1,
                                                                                        ),
                                                                                        marginLeft: theme.spacing(
                                                                                            1,
                                                                                        ),
                                                                                        overflowY:
                                                                                            'scroll',
                                                                                        flex: 1,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        displayName
                                                                                    }
                                                                                </span>

                                                                                <Container
                                                                                    style={{
                                                                                        display:
                                                                                            'grid',
                                                                                        gridTemplateColumns:
                                                                                            'repeat(auto-fill, 170px)',
                                                                                        gap: theme.spacing(
                                                                                            1,
                                                                                        ),
                                                                                        padding: theme.spacing(
                                                                                            1,
                                                                                        ),
                                                                                        paddingBottom: 0,
                                                                                        paddingRight: 0,
                                                                                        alignItems:
                                                                                            'start',
                                                                                        overflowX:
                                                                                            'auto',
                                                                                        flex: 2,
                                                                                    }}
                                                                                >
                                                                                    {issue.message.attachments.map(
                                                                                        (
                                                                                            attachment,
                                                                                        ) => (
                                                                                            <div
                                                                                                key={`ATTACHMENT_${attachment.id}`}
                                                                                                style={{
                                                                                                    display:
                                                                                                        'flex',
                                                                                                    justifyContent:
                                                                                                        'center',
                                                                                                    alignItems:
                                                                                                        'center',
                                                                                                    width: 170,
                                                                                                    height: 170,
                                                                                                }}
                                                                                            >
                                                                                                <Attachment
                                                                                                    attachment={
                                                                                                        attachment.file
                                                                                                    }
                                                                                                    uploaded={
                                                                                                        attachment.uploaded
                                                                                                    }
                                                                                                    theme={
                                                                                                        theme
                                                                                                    }
                                                                                                    size={
                                                                                                        150
                                                                                                    }
                                                                                                />
                                                                                            </div>
                                                                                        ),
                                                                                    )}
                                                                                </Container>
                                                                            </Container>
                                                                        )
                                                                    },
                                                                )}
                                                            </Container>
                                                        ),
                                                    )
                                                )}
                                            </Container>
                                        )
                                    }

                                    const startDate = toMMDDYYYY(
                                        new Date(lease.start_date),
                                    )
                                    const endDate = toMMDDYYYY(
                                        new Date(lease.end_date),
                                    )
                                    const issueCount = calculateIssueCount(
                                        lease,
                                    )

                                    return (
                                        <Container
                                            key={`LEASE_${lease.id}`}
                                            style={{
                                                borderRadius:
                                                    theme.shape.borderRadius,
                                                border: `3px solid ${theme.palette.grey[400]}`,
                                                flexDirection: 'column',
                                                flex: 1,
                                                marginBottom: theme.spacing(2),
                                            }}
                                        >
                                            {/* Tenant Info Header */}
                                            <Container
                                                style={{
                                                    borderRadius:
                                                        theme.shape
                                                            .borderRadius,
                                                    borderBottom: `3px solid ${theme.palette.grey[400]}`,
                                                    padding: theme.spacing(2),
                                                    flexDirection: 'column',
                                                    backgroundColor:
                                                        theme.palette.grey[50],
                                                }}
                                            >
                                                <Container>
                                                    <span
                                                        style={{
                                                            ...typographyStyle(
                                                                theme.typography
                                                                    .h6,
                                                            ),
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {lease.user.name} (
                                                        {issueCount} Issues)
                                                    </span>
                                                </Container>
                                                <Container
                                                    style={{
                                                        marginTop: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    <Container
                                                        style={{
                                                            flexDirection:
                                                                'column',
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                marginBottom: theme.spacing(
                                                                    1,
                                                                ),
                                                            }}
                                                        >
                                                            Email:{' '}
                                                            {lease.user.email}
                                                        </span>
                                                        <span>
                                                            Date: {startDate} -{' '}
                                                            {endDate}
                                                        </span>
                                                    </Container>
                                                </Container>
                                            </Container>

                                            {/* Main Lease Content */}
                                            {renderLeaseContent(lease)}

                                            {/* Child Leases */}
                                            {lease.children?.map((childLease) =>
                                                renderLeaseContent(childLease),
                                            )}
                                        </Container>
                                    )
                                })
                            )}
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
